import React, { ReactElement, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@ui/components/ui/button';
import { Checkbox } from '@ui/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ui/components/ui/dialog';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';
import { MultiSelectField } from '@ui/components/ui/multi-select';
import { Textarea } from '@ui/components/ui/textarea';

import { productPath } from '@app/constants/url';
import { capitalize } from '@app/utils';
import { Roles } from '@app/utils/types';
import { MembershipStatus, ProductStatus } from '@data';
import { useListClientCompanies } from '@data/hooks/clientCompanies';
import { useListPmMembers } from '@data/hooks/membership';
import { useCreateProduct } from '@data/hooks/products';

import {
  ConnectAppsRows,
  connectionsToPayload,
  mergeConnections,
  ProductConnections,
} from '../productDetail/ConnectedApps';
import { toProductUrl } from '../productUrl';

const NO_CLIENT = 'none';
const STATUSES = [ProductStatus.active, ProductStatus.support, ProductStatus.archived, ProductStatus.deactivated];

type TeamDraftRow = { membershipId: string; name: string; isManager: boolean; rndShare: string };

const nameOf = (person: { firstName?: string; lastName?: string }): string =>
  `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim();

const fieldLabel = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

/** The mark beside a field that has to be filled in, as every other dialog draws it. */
const Req = (): ReactElement => <span className="text-destructive">*</span>;

/**
 * Adding a product, asking for the same things its page shows so nothing has to be filled in twice. One
 * request writes the product and its team together, and the new product's page is where it lands.
 */
const AddProductDialog = ({ organizationId }: { organizationId: string }): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // The client COMPANY the product is done for. Not the client people: those are granted read
  // access per person on the client's own page, which is a different question from whose product it is.
  const { data: clientCompanies } = useListClientCompanies(organizationId);
  // Only people who still work here are offered, which is also what the API accepts.
  const { data: members } = useListPmMembers(organizationId, { userStatus: MembershipStatus.active });
  const create = useCreateProduct(organizationId);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [projectCode, setProjectCode] = useState('');
  const [clientCompanyId, setClientCompanyId] = useState(NO_CLIENT);
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState<ProductStatus>(ProductStatus.active);
  const [billable, setBillable] = useState('yes');
  const [team, setTeam] = useState<TeamDraftRow[]>([]);
  const [failed, setFailed] = useState<string | null>(null);
  // What the product will be connected to. Written by the same rows its page uses, and carried by the
  // one create request rather than a PATCH afterwards.
  const [connections, setConnections] = useState<ProductConnections>({ repositories: [] });

  // The fade at the foot of the form, shown while there is more to scroll to.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(true);
  const syncScroll = (): void => {
    const element = scrollRef.current;
    if (element) setAtBottom(element.scrollHeight - element.scrollTop - element.clientHeight < 4);
  };
  useEffect(() => {
    if (!open) return undefined;
    // After the dialog has laid out, not during: the measurement is meaningless before it has height.
    const frame = requestAnimationFrame(syncScroll);
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const addOptions = (members?.items ?? [])
    .filter(membership => membership.role !== Roles.client)
    .filter(membership => !team.some(row => row.membershipId === membership.id))
    .map(membership => ({ value: membership.id, label: nameOf(membership.user) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const clientOptions = (clientCompanies?.items ?? [])
    .map(company => ({ value: company.id, label: company.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const reset = (): void => {
    setName('');
    setProjectCode('');
    setClientCompanyId(NO_CLIENT);
    setDescription('');
    setUrl('');
    setStatus(ProductStatus.active);
    setBillable('yes');
    setTeam([]);
    setFailed(null);
  };

  const shareOutOfRange = team.some(
    row => row.rndShare !== '' && (Number(row.rndShare) < 0 || Number(row.rndShare) > 100),
  );
  const canSave = name.trim().length > 0 && !shareOutOfRange && !create.isPending;

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!canSave) return;
    setFailed(null);

    create.mutate(
      {
        name: name.trim(),
        status,
        projectCode: projectCode.trim() || undefined,
        description: description.trim() || undefined,
        url: toProductUrl(url) || undefined,
        billable: billable === 'yes',
        clientCompanyId: clientCompanyId === NO_CLIENT ? undefined : clientCompanyId,
        team: team.map(row => ({
          membershipId: row.membershipId,
          rndShare: row.rndShare === '' ? 100 : Number(row.rndShare),
          isManager: row.isManager,
        })),
        ...connectionsToPayload(connections),
      },
      {
        // Straight to the new product, which is where everything this dialog does not ask for is set.
        onSuccess: product => {
          setOpen(false);
          reset();
          navigate(productPath(organizationId, product.id));
        },
        // Kept open with what was typed, because a create that failed has nothing to show yet. The name
        // clash is the one refusal worth naming: it is the field the person has to change.
        onError: error => {
          const reason = (error as { response?: { data?: { reason?: string } } })?.response?.data?.reason;
          setFailed(reason === 'productNameTaken' ? t('addProduct.nameTaken') : t('addProduct.failed'));
        },
      },
    );
  };

  return (
    <>
      <Button type="button" size="sm" onClick={() => setOpen(true)}>
        <Plus className="size-4" />
        {t('addProduct.action')}
      </Button>

      <Dialog
        open={open}
        onOpenChange={isOpen => {
          setOpen(isOpen);
          if (!isOpen) reset();
        }}
      >
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('addProject')}</DialogTitle>
            <DialogDescription>{t('addProjectHint')}</DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="flex min-h-0 flex-col gap-4">
            <div className="relative">
              <div
                ref={scrollRef}
                onScroll={syncScroll}
                className="-mx-1 flex max-h-[60vh] flex-col gap-4 overflow-y-auto px-1 pb-1"
              >
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="add-product-name">
                    {t('titles.name')} <Req />
                  </Label>
                  <Input
                    id="add-product-name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder={t('projectNamePlaceholder')}
                    autoComplete="off"
                    disabled={create.isPending}
                    className="max-w-md bg-popover"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="add-product-code">
                    {t('projectCode')} <span className="font-normal text-muted-foreground">({t('optional')})</span>
                  </Label>
                  <Input
                    id="add-product-code"
                    value={projectCode}
                    onChange={e => setProjectCode(e.target.value)}
                    autoComplete="off"
                    disabled={create.isPending}
                    className="max-w-md bg-popover"
                  />
                </div>

                <MultiSelectField
                  single
                  label={t('client')}
                  options={clientOptions}
                  value={clientCompanyId === NO_CLIENT ? [] : [clientCompanyId]}
                  onChange={vals => setClientCompanyId(vals[0] ?? NO_CLIENT)}
                  emptyLabel={t('productDetail.noClient')}
                  searchPlaceholder={t('search')}
                  emptySearchLabel={t('noMatches')}
                  className="max-w-md"
                />

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="add-product-description">
                    {t('titles.productDescription')}{' '}
                    <span className="font-normal text-muted-foreground">({t('optional')})</span>
                  </Label>
                  <Textarea
                    id="add-product-description"
                    rows={3}
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    disabled={create.isPending}
                    className="bg-popover"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="add-product-url">
                    {t('titles.productUrl')}{' '}
                    <span className="font-normal text-muted-foreground">({t('optional')})</span>
                  </Label>
                  <Input
                    id="add-product-url"
                    value={url}
                    onChange={e => setUrl(e.target.value)}
                    placeholder={t('productUrlPlaceholder')}
                    autoComplete="off"
                    inputMode="url"
                    disabled={create.isPending}
                    className="bg-popover"
                  />
                </div>

                <MultiSelectField
                  single
                  label={
                    <>
                      {t('status')} <Req />
                    </>
                  }
                  options={STATUSES.map(value => ({ value, label: capitalize(value) ?? value }))}
                  value={[status]}
                  onChange={vals => setStatus((vals[0] as ProductStatus) ?? status)}
                  emptyLabel={t('status')}
                  searchable={false}
                  clearable={false}
                  className="max-w-xs"
                />

                <div className="flex flex-col gap-1.5">
                  <Label>
                    {t('billableWork')} <Req />
                  </Label>
                  <div className="inline-flex w-fit rounded-lg border border-input bg-popover p-0.5">
                    {['yes', 'no'].map(value => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setBillable(value)}
                        className={cx(
                          'rounded-md px-3 py-1 text-sm font-medium transition-colors',
                          billable === value
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {t(value)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <span>{t('teamMembers')}</span>
                  {team.length ? (
                    <div className="overflow-x-auto rounded-lg border border-border">
                      <div className="min-w-[26rem]">
                        <div className={`flex items-center gap-4 border-b border-border px-3 py-2 ${fieldLabel}`}>
                          <span className="min-w-0 flex-1">{t('member')}</span>
                          <span className="w-20 shrink-0 text-center">{t('manager')}</span>
                          <span className="w-24 shrink-0">{t('rnd')}</span>
                          <span className="w-8 shrink-0" />
                        </div>
                        <div className="divide-y divide-border">
                          {team.map(row => (
                            <div key={row.membershipId} className="flex items-center gap-4 px-3 py-2.5">
                              <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                                {row.name}
                              </span>
                              <span className="flex w-20 shrink-0 justify-center">
                                <Checkbox
                                  aria-label={`${row.name}, ${t('manager')}`}
                                  checked={row.isManager}
                                  onCheckedChange={checked =>
                                    setTeam(prev =>
                                      prev.map(other =>
                                        other.membershipId === row.membershipId
                                          ? { ...other, isManager: checked === true }
                                          : other,
                                      ),
                                    )
                                  }
                                />
                              </span>
                              <div className="flex w-24 shrink-0 items-center gap-1.5">
                                <Input
                                  type="number"
                                  min={0}
                                  max={100}
                                  aria-label={`${row.name}, ${t('rndShare')}`}
                                  value={row.rndShare}
                                  onChange={e =>
                                    setTeam(prev =>
                                      prev.map(other =>
                                        other.membershipId === row.membershipId
                                          ? { ...other, rndShare: e.target.value }
                                          : other,
                                      ),
                                    )
                                  }
                                  className="w-16"
                                />
                                <span className="text-sm text-muted-foreground">%</span>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={`${t('actions.remove')} ${row.name}`}
                                onClick={() =>
                                  setTeam(prev => prev.filter(other => other.membershipId !== row.membershipId))
                                }
                                className="shrink-0 text-muted-foreground hover:text-foreground"
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <MultiSelectField
                    single
                    label={<span className="sr-only">{t('addPerson')}</span>}
                    options={addOptions}
                    value={[]}
                    onChange={vals => {
                      const membershipId = vals[0];
                      const option = addOptions.find(candidate => candidate.value === membershipId);
                      if (!option) return;
                      // Everybody starts wholly R&D, which is the common case; the share is edited from there.
                      setTeam(prev => [
                        ...prev,
                        { membershipId, name: option.label, isManager: false, rndShare: '100' },
                      ]);
                    }}
                    emptyLabel={t('addPerson')}
                    searchPlaceholder={t('search')}
                    emptySearchLabel={t('noMatches')}
                    className="max-w-xs"
                  />
                  <p className="text-xs text-muted-foreground">{t('addProduct.teamHint')}</p>
                </div>

                <div className="flex flex-col gap-2">
                  <span>
                    {t('connectedApps.title')}{' '}
                    <span className="font-normal text-muted-foreground">({t('optional')})</span>
                  </span>
                  <ConnectAppsRows
                    product={connections}
                    productName={name}
                    source={{ organizationId }}
                    canEdit
                    isPending={create.isPending}
                    patch={values => setConnections(current => mergeConnections(current, values))}
                  />
                </div>
              </div>
              {/* Says the form goes on. Without it the dialog looks as though it ends at whatever field
                  happens to sit on the fold. */}
              {!atBottom && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-popover to-transparent" />
              )}
            </div>

            {failed ? <p className="text-sm text-destructive">{failed}</p> : null}
            {shareOutOfRange ? <p className="text-sm text-destructive">{t('productDetail.shareOutOfRange')}</p> : null}

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={create.isPending}>
                {t('actions.cancel')}
              </Button>
              <Button type="submit" disabled={!canSave}>
                {t('createProject')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddProductDialog;
